import React from "react";
import { Youtube, ExternalLink, Play } from "lucide-react";
import { TiktokIcon } from "./icons/TiktokIcon";

const TIKTOK_URL = "https://www.tiktok.com/@chudyprzemyslaw";
const TIKTOK_HANDLE = "@chudyprzemyslaw";
const YOUTUBE_URL = "https://www.youtube.com/@chudyprzemyslaw";
const YOUTUBE_HANDLE = "@chudyprzemyslaw";

const topics = [
  "Kulisy budowania firmy transportowej",
  "Rentowność, marże i koszty — bez owijania w bawełnę",
  "Błędy, które widzę u 90% spedycji",
  "Jak rozmawiać z klientem i ustawiać stawki",
  "Restrukturyzacja i wychodzenie z kryzysu",
  "Narzędzia, które naprawdę działają w praktyce",
];

export default function Videos() {
  return (
    <section data-testid="videos-section" className="section relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[#0c0c0e]" />
      <div className="hero-glow top-[10%] right-[-10%]" />

      <div className="container-x relative z-10">
        {/* Top platforms grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
          {/* TikTok card */}
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-platform-tiktok"
            className="card-dark p-10 lg:p-12 block group relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gradient-to-br from-[#25F4EE]/10 via-[#FE2C55]/10 to-transparent blur-3xl" />
            <div className="relative z-10 flex items-start justify-between gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 border border-[rgba(212,169,74,0.35)] flex items-center justify-center">
                    <TiktokIcon size={22} className="text-[var(--gold)]" />
                  </div>
                  <span className="font-display text-[14px] tracking-[0.3em] text-[var(--gold)]">
                    TikTok
                  </span>
                </div>
                <h3 className="h-display text-[28px] md:text-[34px] mt-8 text-[#f5f1e8]">
                  Krótkie rolki z realiów branży
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-[#b8b3a7]">
                  Konkret, bez lania wody. Przykłady z życia, liczby i kulisy pracy
                  w spedycji i transporcie.
                </p>
                <div className="mt-8 flex items-center gap-3">
                  <span className="text-[11px] tracking-[0.28em] uppercase gold-text">
                    {TIKTOK_HANDLE}
                  </span>
                  <ExternalLink size={14} className="text-[var(--gold)] group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
              <Play size={44} strokeWidth={1} className="text-[var(--gold)] shrink-0 opacity-80" />
            </div>
          </a>

          {/* YouTube card */}
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-platform-youtube"
            className="card-dark p-10 lg:p-12 block group relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gradient-to-br from-red-600/10 via-red-500/5 to-transparent blur-3xl" />
            <div className="relative z-10 flex items-start justify-between gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 border border-[rgba(212,169,74,0.35)] flex items-center justify-center">
                    <Youtube size={22} strokeWidth={1.4} className="text-[var(--gold)]" />
                  </div>
                  <span className="font-display text-[14px] tracking-[0.3em] text-[var(--gold)]">
                    YouTube
                  </span>
                </div>
                <h3 className="h-display text-[28px] md:text-[34px] mt-8 text-[#f5f1e8]">
                  Dłuższe rozmowy i analizy
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-[#b8b3a7]">
                  Głębsze wejścia w tematy: strategia firmy, zarządzanie flotą,
                  case studies, rozmowy z branżą.
                </p>
                <div className="mt-8 flex items-center gap-3">
                  <span className="text-[11px] tracking-[0.28em] uppercase gold-text">
                    {YOUTUBE_HANDLE}
                  </span>
                  <ExternalLink size={14} className="text-[var(--gold)] group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
              <Play size={44} strokeWidth={1} className="text-[var(--gold)] shrink-0 opacity-80" />
            </div>
          </a>
        </div>

        {/* Topics */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">O czym nagrywam</span>
            <h2 className="h-display mt-4 text-[clamp(28px,3.6vw,44px)]">
              Wiedza z drogi, <span className="gold-gradient-text italic">nie z książek</span>
            </h2>
            <div className="gold-line mt-6" />
            <p className="mt-8 text-[16px] leading-relaxed text-[#b8b3a7]">
              Nie kręcę „content marketingu dla contentu". Każdy materiał to
              coś, co sam widziałem, wdrażałem albo naprawiałem — w realnych firmach,
              na realnych pieniądzach.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-[rgba(212,169,74,0.12)] border-t border-b border-[rgba(212,169,74,0.12)]">
              {topics.map((t, i) => (
                <li
                  key={t}
                  data-testid={`video-topic-${i}`}
                  className="flex items-center gap-6 py-5 group"
                >
                  <span className="font-display text-[14px] tracking-[0.3em] text-[var(--gold)] shrink-0 w-10">
                    0{i + 1}
                  </span>
                  <span className="flex-1 text-[16px] text-[#e8e3d5] group-hover:text-white transition-colors">
                    {t}
                  </span>
                  <Play size={16} className="text-[var(--gold)] opacity-0 group-hover:opacity-100 transition-opacity" />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Follow strip */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 text-center">
          <p className="text-[14px] text-[#b8b3a7]">
            Nie przegap nowych materiałów — obserwuj:
          </p>
          <div className="flex gap-3 flex-wrap justify-center">
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-follow-tiktok"
              className="btn-ghost"
            >
              <TiktokIcon size={14} />
              TikTok
            </a>
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-follow-youtube"
              className="btn-ghost"
            >
              <Youtube size={14} />
              YouTube
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
